import * as React from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';

import { Input } from '@/components/ui/input';
import type { FieldVariant } from '@/lib/field-chrome';

type PasswordInputProps = Omit<React.ComponentProps<typeof Input>, 'type' | 'icon' | 'trailing'> & {
  /** When `false`, hides the left-side lock icon. Defaults to `true`. */
  showLockIcon?: boolean;
  /** When `false`, hides the eye/eye-off visibility toggle. Defaults to `true`. */
  showToggle?: boolean;
  /** Visual variant. Forwarded to the underlying `Input`. */
  variant?: FieldVariant;
};

/**
 * A password `Input` with a left-side lock icon and a right-side show/hide
 * toggle. The visibility state is owned internally — callers don't manage it.
 *
 * ```tsx
 * <PasswordInput placeholder="••••••••" {...field} />
 * ```
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ showLockIcon = true, showToggle = true, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        icon={showLockIcon ? <Lock className="h-4 w-4" /> : undefined}
        trailing={
          showToggle ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setVisible((v) => !v)}
              className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
              aria-label={visible ? 'Hide password' : 'Show password'}
            >
              {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          ) : undefined
        }
        {...props}
      />
    );
  }
);
PasswordInput.displayName = 'PasswordInput';
